import {ActiveField, ActiveFieldOptions} from './ActiveField'
import {CodesList} from './ActiveFormErrors'
// @ts-ignore
import {Dictionary} from 'vuex'
import _ from 'lodash'

type MapFunction = () => string[][]

export interface ActiveFormOptionsStep {
  label?: string
  icon?: string
  map: string[][] | MapFunction
  validator?: () => Promise<boolean>
  disabled?: () => boolean
}

export interface ActiveFormOptions {
  label?: string,
  inputs: Dictionary<ActiveFieldOptions<any>>
  steps: ActiveFormOptionsStep[]
  onSubmit?: (data: any) => any
  submitText?: string
}

export interface ActiveFormError {
  field?: string,
  code?: keyof CodesList
  message?: string
}

export default class ActiveForm {
  public data: any = {}
  public scope: any = {}
  public step: number = 0

  public readonly fields: Dictionary<ActiveField> = {}
  public form: ActiveFormOptions
  public errors: ActiveFormError[] = []

  constructor (form: ActiveFormOptions, initData: any, scope: any) {
    this.form = form
    this.data = initData
    this.scope = scope

    this.initFields(this.form.inputs)
  }

  public async validate (submit = false) {
    this.errors = []
    for (let step = 0; step < this.form.steps.length; step++) {
      if (this.stepDisabled(step)) continue;
      for (const fieldName in this.getStepFields(step)) {
        await this.fields[fieldName].validate(this.data[fieldName], submit)
      }
    }
    return this.errors.length === 0
  }

  public getStepMap (step: number) {
    let stepMap: string[][] = []

    if (Array.isArray(this.form.steps[step].map)) {
      stepMap = this.form.steps[step].map as string[][]
    } else {
      stepMap = (this.form.steps[step].map as MapFunction)()
    }
    return stepMap
  }

  public getStepFields (step: number) {
    const fields: Dictionary<ActiveField> = {}
    let stepMap: string[][] = this.getStepMap(step)
    for (const row of stepMap) {
      for (const fieldName of row) {
        if (this.fields[fieldName]) {
          fields[fieldName] = this.fields[fieldName]
        }
      }
    }
    return fields
  }

  public getFieldErrors (fieldName: string): ActiveFormError[] {
    return _.filter(this.errors, {field: fieldName})
  }

  public fieldsHasErrors (fieldNames: string[]) {
    for (const error of this.errors) {
      if (error.field !== undefined && fieldNames.indexOf(error.field) !== -1) {
        return true
      }
    }
    return false
  }

  public async stepValidate () {
    const fields = this.getStepFields(this.step)
    let valid = true
    for (const fieldName in fields) {
      if (!await fields[fieldName].validate(this.data[fieldName], true)) {
        valid = false
      }
    }
    if (!valid) {
      return false
    }

    if (this.form.steps[this.step].validator) {
      return this.form.steps[this.step].validator!.call(this)
    }
    return true
  }

  public cleanErrors (field: string | undefined | false): void {
    if (!field) {
      this.errors.splice(0, this.errors.length)
      return
    }
    for (let i = this.errors.length - 1; i >= 0; i--) {
      if (this.errors[i].field === field) {
        this.errors.splice(i, 1)
      }
    }
  }

  public addError (error: ActiveFormError): void {
    this.errors.push(error)
  }

  public async nextStep (): Promise<boolean> {
    if (this.stepDisabled(this.step) || await this.stepValidate.call(this)) {
      let newStep = this.step + 1
      while (this.stepDisabled(newStep) && newStep < this.form.steps.length) {
        newStep++
      }
      if (!this.stepDisabled(newStep)) {
        this.step = newStep
        return true
      }
    }
    return false
  }

  public async prevStep (): Promise<boolean> {
    let newStep = this.step - 1
    while (this.stepDisabled(newStep) && newStep >= 0) {
      newStep--
    }
    if (!this.stepDisabled(newStep)) {
      this.step = newStep
      return true
    }
    return false
  }

  public reset () {
    this.step = 0
    this.data = {}
    this.scope = {}
  }

  public stepDisabled (step: number) {
    return !this.form.steps[step] || (this.form.steps[step] && (this.form.steps[step].disabled && this.form.steps[step].disabled!.call(this))) || false
  }

  private initFields (inputs: any) {
    for (const fieldName in inputs) {
      if (!inputs.hasOwnProperty(fieldName)) {
        continue
      }
      if (inputs[fieldName].type) {
        const FieldConstructor = inputs[fieldName].type
        this.fields[fieldName] = new FieldConstructor(fieldName, inputs[fieldName], this)
      }
    }
  }

}